import React from 'react';
import { Button, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import type { ChangeExtraFields, ChangeExtraValue, ChangeFormField } from '../../../types/changes';
import { extraFieldVisibleFromNatives, weekdayConstraintHint } from '../changeFormSchema';
import { groupFieldsByFormTab, normalizeFormTabsPosition } from '../changeInternalSubtaskFormTabs';
import {
  checklistUsesThreeColumnGrid,
  formatYesNoDisplayLabel,
  layoutChecklistFields,
  type ChecklistLayoutItem,
} from '../changeSubtaskChecklistLayout';
import ChangeExtraFieldInput from './ChangeExtraFieldInput';
import './ChangeSubtaskChecklist.css';

interface Props {
  fields: ChangeFormField[];
  tabs?: string[];
  tabsPosition?: string | null;
  extras: ChangeExtraFields;
  onChange: (fieldId: number, value: ChangeExtraValue) => void;
  onSave: () => void;
  saving?: boolean;
  readOnly?: boolean;
  groupName?: string | null;
  waitingMessage?: string | null;
}

const colProps = (span: number) => (span === 6 ? { xs: 12 as const, md: 6 as const } : { xs: 12 as const });

const isCurrencyExtra = (
  value: object,
): value is { currency: string; value: string } => 'currency' in value;

const isPeriodExtra = (
  value: object,
): value is { start: string; end: string } => 'start' in value || 'end' in value;

const isFilledAnswer = (value: ChangeExtraValue): boolean => {
  if (value == null) return false;
  if (typeof value !== 'object') return String(value).trim() !== '';
  if (isCurrencyExtra(value)) return String(value.value || '').trim() !== '';
  if (isPeriodExtra(value)) return Boolean(value.start?.trim() || value.end?.trim());
  return false;
};

export const formatChecklistAnswer = (field: ChangeFormField, value: ChangeExtraValue): string => {
  if (!isFilledAnswer(value)) return 'Ainda sem resposta';
  if (field.field_type === 'yes_no') {
    const label = formatYesNoDisplayLabel(field, value);
    return label || 'Ainda sem resposta';
  }
  if (typeof value === 'object' && value) {
    if (isCurrencyExtra(value)) {
      const amount = String(value.value || '').trim();
      const currency = String(value.currency || 'BRL').toUpperCase();
      if (!amount) return 'Ainda sem resposta';
      return `${currency} ${amount}`;
    }
    if (isPeriodExtra(value)) {
      const start = value.start?.trim() || '';
      const end = value.end?.trim() || '';
      if (start && end) return `${start} — ${end}`;
      return start || end;
    }
  }
  return String(value);
};

const FieldMetaHints: React.FC<{ field: ChangeFormField }> = ({ field }) => (
  <>
    {field.help_text ? <Form.Text className="text-muted">{field.help_text}</Form.Text> : null}
    {weekdayConstraintHint(field.weekday_constraint) ? (
      <Form.Text className="text-muted d-block">{weekdayConstraintHint(field.weekday_constraint)}</Form.Text>
    ) : null}
  </>
);

const ReadonlyYesNoRow: React.FC<{
  field: ChangeFormField;
  followers: ChangeFormField[];
  extras: ChangeExtraFields;
  allFields: ChangeFormField[];
  threeColumnGrid: boolean;
}> = ({ field, followers, extras, allFields, threeColumnGrid }) => {
  const answer = formatChecklistAnswer(field, extras[String(field.id)]);
  const visibleFollowers = followers.filter((item) =>
    extraFieldVisibleFromNatives(item, allFields, {}, extras),
  );
  return (
    <Row className="change-subtask-checklist-row align-items-start g-2 g-lg-3">
      <Col xs={12} lg={threeColumnGrid ? 4 : 6}>
        <dt className="small text-muted mb-1">{field.label}</dt>
      </Col>
      <Col xs={12} lg={threeColumnGrid ? 3 : 6} className="change-subtask-checklist-row__options-col">
        <dd className="change-subtask-checklist-readonly__value mb-0">{answer}</dd>
      </Col>
      {threeColumnGrid ? (
        <Col xs={12} lg={5} className="change-subtask-checklist-row__follower-col">
          {visibleFollowers.length ? (
            visibleFollowers.map((follower) => (
              <div key={follower.id} className="mb-2 mb-lg-0">
                <dt className="small text-muted mb-1">{follower.label}</dt>
                <dd className="change-subtask-checklist-readonly__value mb-0">
                  {formatChecklistAnswer(follower, extras[String(follower.id)])}
                </dd>
              </div>
            ))
          ) : null}
        </Col>
      ) : null}
    </Row>
  );
};

const EditYesNoRow: React.FC<{
  field: ChangeFormField;
  followers: ChangeFormField[];
  allFields: ChangeFormField[];
  extras: ChangeExtraFields;
  onChange: (fieldId: number, value: ChangeExtraValue) => void;
  threeColumnGrid: boolean;
}> = ({ field, followers, allFields, extras, onChange, threeColumnGrid }) => {
  const visibleFollowers = followers.filter((item) =>
    extraFieldVisibleFromNatives(item, allFields, {}, extras),
  );
  return (
    <Row className="change-subtask-checklist-row align-items-start g-2 g-lg-3">
      <Col xs={12} lg={threeColumnGrid ? 4 : 6}>
        <div className="change-subtask-checklist-row__label">
          {field.label}
          {field.is_required ? <span className="text-danger"> *</span> : null}
        </div>
        <FieldMetaHints field={field} />
      </Col>
      <Col xs={12} lg={threeColumnGrid ? 3 : 6} className="change-subtask-checklist-row__options-col">
        <div className="change-subtask-checklist-row__options">
          <ChangeExtraFieldInput
            field={field}
            value={extras[String(field.id)]}
            onChange={(value) => onChange(field.id, value)}
          />
        </div>
      </Col>
      {threeColumnGrid ? (
        <Col xs={12} lg={5} className="change-subtask-checklist-row__follower-col">
          {visibleFollowers.map((follower) => (
            <div key={follower.id} className="mb-3 mb-lg-0">
              <div className="change-subtask-checklist-row__follower-label">
                {follower.label}
                {follower.is_required ? <span className="text-danger"> *</span> : null}
              </div>
              <ChangeExtraFieldInput
                field={follower}
                value={extras[String(follower.id)]}
                onChange={(value) => onChange(follower.id, value)}
              />
              <FieldMetaHints field={follower} />
            </div>
          ))}
        </Col>
      ) : null}
    </Row>
  );
};

const renderReadonlyItem = (
  item: ChecklistLayoutItem,
  extras: ChangeExtraFields,
  fields: ChangeFormField[],
  threeColumnGrid: boolean,
) => {
  if (item.kind === 'yes_no_row') {
    return (
      <ReadonlyYesNoRow
        key={item.field.id}
        field={item.field}
        followers={item.followers}
        extras={extras}
        allFields={fields}
        threeColumnGrid={threeColumnGrid}
      />
    );
  }
  return (
    <Row key={item.field.id} className="change-subtask-checklist-row align-items-start g-2">
      <Col xs={12}>
        <dt className="small text-muted mb-1">{item.field.label}</dt>
        <dd className="change-subtask-checklist-readonly__value mb-0">
          {formatChecklistAnswer(item.field, extras[String(item.field.id)])}
        </dd>
      </Col>
    </Row>
  );
};

const renderEditItem = (
  item: ChecklistLayoutItem,
  fields: ChangeFormField[],
  extras: ChangeExtraFields,
  onChange: (fieldId: number, value: ChangeExtraValue) => void,
  threeColumnGrid: boolean,
) => {
  if (item.kind === 'yes_no_row') {
    return (
      <EditYesNoRow
        key={item.field.id}
        field={item.field}
        followers={item.followers}
        allFields={fields}
        extras={extras}
        onChange={onChange}
        threeColumnGrid={threeColumnGrid}
      />
    );
  }
  const field = item.field;
  return (
    <Col key={field.id} {...colProps(field.col_span)}>
      <Form.Label>
        {field.label}
        {field.is_required ? ' *' : ''}
      </Form.Label>
      <ChangeExtraFieldInput
        field={field}
        value={extras[String(field.id)]}
        onChange={(value) => onChange(field.id, value)}
      />
      <FieldMetaHints field={field} />
    </Col>
  );
};

const renderEditBlock = (
  blockFields: ChangeFormField[],
  allFields: ChangeFormField[],
  extras: ChangeExtraFields,
  onChange: (fieldId: number, value: ChangeExtraValue) => void,
) => {
  const visibleFields = blockFields.filter((field) =>
    extraFieldVisibleFromNatives(field, allFields, {}, extras),
  );
  if (!visibleFields.length) return null;
  const threeColumnGrid = checklistUsesThreeColumnGrid(blockFields);
  const layout = layoutChecklistFields(blockFields, visibleFields);
  const rowItems = layout.filter((item) => item.kind === 'yes_no_row');
  const standaloneItems = layout.filter((item) => item.kind === 'standalone');
  return (
    <>
      <div className={threeColumnGrid ? 'change-subtask-checklist--three-col' : undefined}>
        {rowItems.map((item) =>
          item.kind === 'yes_no_row'
            ? renderEditItem(item, allFields, extras, onChange, threeColumnGrid)
            : null,
        )}
      </div>
      {standaloneItems.length ? (
        <Row className="g-2 mt-1">
          {standaloneItems.map((item) =>
            item.kind === 'standalone'
              ? renderEditItem(item, allFields, extras, onChange, threeColumnGrid)
              : null,
          )}
        </Row>
      ) : null}
    </>
  );
};

const ChangeSubtaskChecklist: React.FC<Props> = ({
  fields,
  tabs,
  tabsPosition,
  extras,
  onChange,
  onSave,
  saving = false,
  readOnly = false,
  groupName,
  waitingMessage,
}) => {
  const safeFields = fields ?? [];
  if (!safeFields.length) return null;

  const visibleFields = safeFields.filter((field) =>
    extraFieldVisibleFromNatives(field, safeFields, {}, extras),
  );
  if (!visibleFields.length) return null;

  const grouped = groupFieldsByFormTab(safeFields, tabs);
  const threeColumnGrid = checklistUsesThreeColumnGrid(safeFields);
  const layout = layoutChecklistFields(safeFields, visibleFields);

  if (readOnly) {
    const answered = visibleFields.filter((field) => isFilledAnswer(extras[String(field.id)]));
    const waitingText =
      waitingMessage?.trim() ||
      (groupName?.trim()
        ? `Aguardando um membro de ${groupName.trim()} responder. O resultado aparece aqui; quem criou a mudança não preenche estas perguntas.`
        : 'Aguardando um membro deste grupo responder. O resultado aparece aqui; quem criou a mudança não preenche estas perguntas.');
    return (
      <div className="border rounded p-3 mb-3 bg-white">
        <h6 className="mb-3">{groupName?.trim() ? `Checklist de ${groupName.trim()}` : 'Checklist deste grupo'}</h6>
        {answered.length === 0 ? (
          <p className="small text-muted mb-0">{waitingText}</p>
        ) : (
          <dl
            className={`mb-0${threeColumnGrid ? ' change-subtask-checklist--three-col' : ''}`}
          >
            {layout.map((item) => renderReadonlyItem(item, extras, safeFields, threeColumnGrid))}
          </dl>
        )}
      </div>
    );
  }

  return (
    <div className="border rounded p-3 mb-3 bg-light">
      <h6 className="mb-3">Perguntas do checklist</h6>
      {(() => {
        const tabsBlock = grouped.tabs.length ? (
          <Tabs
            defaultActiveKey={grouped.tabs[0].name}
            className={normalizeFormTabsPosition(tabsPosition) === 'before' ? 'mb-3' : 'mt-3 mb-2'}
            justify
          >
            {grouped.tabs.map((tab) => (
              <Tab key={tab.name} eventKey={tab.name} title={tab.name}>
                <div className="pt-3">
                  {tab.fields.length
                    ? renderEditBlock(tab.fields, safeFields, extras, onChange)
                    : (
                      <p className="small text-muted mb-0">
                        Nenhum campo nesta aba. Inclua no cadastro da subtarefa interna.
                      </p>
                    )}
                </div>
              </Tab>
            ))}
          </Tabs>
        ) : null;
        const questionsBlock = renderEditBlock(grouped.main, safeFields, extras, onChange);
        return normalizeFormTabsPosition(tabsPosition) === 'before' ? (
          <>
            {tabsBlock}
            {questionsBlock}
          </>
        ) : (
          <>
            {questionsBlock}
            {tabsBlock}
          </>
        );
      })()}
      <Button size="sm" className="mt-3" disabled={saving} onClick={() => void onSave()}>
        {saving ? 'Salvando...' : 'Salvar respostas'}
      </Button>
    </div>
  );
};

export default ChangeSubtaskChecklist;
